import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Clock, Star, ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { useState } from 'react';
import BookingModal from '../components/BookingModal';

// URL to location slug mapping
const urlToSlugMap = {
  '/nails-rr-nagar': 'rr-nagar',
  '/nails-basaveshwarnagar': 'basaveshwarnagar',
  '/nails-sahakarnagar': 'sahakarnagar',
  '/nails-varthur': 'varthur',
  '/nails-sarjapur-road': 'sarjapur-road',
  '/nails-gunjur': 'gunjur',
  '/nail-salon-rr-nagar': 'rr-nagar',
  '/nail-salon-basaveshwarnagar': 'basaveshwarnagar',
  '/nail-salon-varthur': 'varthur',
  '/manicure-pedicure-bangalore': 'rr-nagar'
};

// SEO Location Data - Comprehensive keyword targeting
const locationSEOData = {
  'rr-nagar': {
    name: 'RR Nagar',
    fullName: 'Rajarajeshwari Nagar',
    slug: 'rr-nagar',
    title: 'Best Nail Salon in RR Nagar, Bangalore | Nail Art, Manicure, Pedicure',
    metaDescription: 'Looking for the best nail salon in RR Nagar? BLR Nail Club offers premium nail extensions, gel nails, nail art, manicure, pedicure, lash extensions & hair services. Book now!',
    keywords: 'nail salon RR Nagar, best nail salon Rajarajeshwari Nagar, nail art RR Nagar, manicure pedicure RR Nagar, nail extensions RR Nagar, gel nails RR Nagar, beauty salon RR Nagar, hair salon RR Nagar, lash extensions RR Nagar, nail spa RR Nagar Bangalore',
    h1: 'Best Nail Salon in RR Nagar',
    h2: 'Premium Nail Art, Manicure & Pedicure Services',
    address: 'N2VK Arcade, 1154, Jawaharlal Nehru Rd, BEML Layout 3rd Stage, Rajarajeshwari Nagar, Bengaluru 560098',
    phone: '+91 7204602211',
    timings: '10:00 AM - 8:00 PM',
    hasStore: true,
    nearbyAreas: ['Kengeri', 'Nagarbhavi', 'Vijayanagar', 'Mysore Road', 'BEML Layout'],
    heroImage: 'https://images.pexels.com/photos/3997390/pexels-photo-3997390.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '12.9267',
    lng: '77.5192',
    content: {
      intro: "Welcome to BLR Nail Club RR Nagar - Rajarajeshwari Nagar's most trusted nail and beauty destination. Located in the heart of RR Nagar, we've been serving the community with premium nail services, stunning nail art, and professional beauty treatments.",
      whyChoose: [
        "Certified nail technicians with 5+ years experience",
        "Premium quality products (OPI, Gelish, CND)",
        "Hygienic practices with sterilized tools",
        "Trendy nail art designs updated monthly",
        "Affordable pricing with no hidden charges",
        "Convenient location near RR Nagar main road"
      ],
      popularServices: [
        { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Classic Manicure', price: '₹499', duration: '45 mins' },
        { name: 'Spa Pedicure', price: '₹799', duration: '60 mins' },
        { name: 'Nail Art (per nail)', price: '₹100', duration: '15 mins' },
        { name: 'Acrylic Extensions', price: '₹1,999', duration: '120 mins' },
        { name: 'French Tips', price: '₹899', duration: '60 mins' }
      ]
    }
  },
  'basaveshwarnagar': {
    name: 'Basaveshwarnagar',
    fullName: 'Basaveshwarnagar',
    slug: 'basaveshwarnagar',
    title: 'Best Nail Salon in Basaveshwarnagar | Nail Art, Extensions, Manicure',
    metaDescription: 'Top-rated nail salon in Basaveshwarnagar, Bangalore. Expert nail extensions, nail art, gel nails, manicure & pedicure services. Premium quality at affordable prices. Book today!',
    keywords: 'nail salon Basaveshwarnagar, best nail art Basaveshwarnagar, manicure pedicure Basaveshwarnagar, nail extensions Basaveshwarnagar, gel nails Basaveshwarnagar, beauty salon Basaveshwarnagar, hair salon Basaveshwarnagar, lash extensions Basaveshwarnagar, nail spa Basaveshwarnagar, Nailashes Basaveshwarnagar',
    h1: 'Best Nail Salon in Basaveshwarnagar',
    h2: 'Expert Nail Extensions, Art & Beauty Services',
    address: 'LGF, No. 9, East Jayanagar, West of Chord Road 1st Stage, Basaveshwar Nagar, Bengaluru 560079',
    phone: '+91 9611560759',
    googleProfileUrl: 'https://share.google/awsG9CL1kTJ5WFYSy',
    timings: '10:30 AM - 10:00 PM',
    hasStore: true,
    rating: '4.8',
    reviewCount: '529',
    nearbyAreas: ['Rajajinagar', 'Vijayanagar', 'Mahalakshmi Layout', 'Kamakshipalya', 'Nandini Layout'],
    heroImage: 'https://images.pexels.com/photos/3997391/pexels-photo-3997391.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '12.9916',
    lng: '77.5369',
    content: {
      intro: "Discover Nailashes Basaveshwarnagar - your premier destination for nail artistry and beauty services in West Bangalore. Our Basaveshwarnagar studio offers a relaxing ambiance with skilled nail technicians ready to transform your nails.",
      whyChoose: [
        "4.8 rating with 529+ Google reviews",
        "Latest nail art trends from Instagram & Pinterest",
        "Premium imported nail products",
        "Comfortable AC studio with free WiFi",
        "Easy parking available",
        "Walk-ins welcome, appointments preferred"
      ],
      popularServices: [
        { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Luxury Manicure', price: '₹699', duration: '60 mins' },
        { name: 'Spa Pedicure', price: '₹799', duration: '60 mins' },
        { name: '3D Nail Art', price: '₹150', duration: '20 mins' },
        { name: 'Chrome Nails', price: '₹1,299', duration: '75 mins' },
        { name: 'Polygel Extensions', price: '₹1,799', duration: '100 mins' }
      ]
    }
  },
  'sahakarnagar': {
    name: 'Sahakarnagar',
    fullName: 'Sahakarnagar',
    slug: 'sahakarnagar',
    title: 'Best Nail Salon in Sahakarnagar, Bangalore | Nail Art & Extensions',
    metaDescription: 'Visit BLR Nail Club in Sahakarnagar for premium nail services. Expert nail extensions, gel nails, nail art, manicure & pedicure. Located on Sahakarnagar Main Road. Book now!',
    keywords: 'nail salon Sahakarnagar, best nail art Sahakarnagar, manicure pedicure Sahakarnagar, nail extensions Sahakarnagar, gel nails Sahakarnagar, beauty salon Sahakarnagar, hair salon Sahakarnagar, nail spa Sahakarnagar Bangalore, nails near Sahakarnagar, nail salon North Bangalore',
    h1: 'Best Nail Salon in Sahakarnagar',
    h2: 'Premium Nail Art & Beauty Services - Now Open!',
    address: 'Ground Floor, 332, Sahakarnagar Main Rd, next to Bounce, Park View Layout, Byatarayanapura, Bengaluru 560092',
    phone: '+91 7204604503',
    googleProfileUrl: 'https://maps.app.goo.gl/kybq7sBMn9wZwCZM6',
    timings: '10:00 AM - 8:00 PM',
    hasStore: true,
    comingSoon: false,
    nearbyAreas: ['Hebbal', 'Yelahanka', 'RT Nagar', 'Kodigehalli', 'Jakkur', 'Byatarayanapura', 'Park View Layout'],
    heroImage: 'https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '13.0565',
    lng: '77.5794',
    content: {
      intro: "BLR Nail Club is now open in Sahakarnagar! Located on Sahakarnagar Main Road next to Bounce, our newest studio brings premium nail services to North Bangalore. Experience world-class nail artistry right in your neighborhood.",
      whyChoose: [
        "Convenient location on Sahakarnagar Main Road",
        "Expert nail technicians with years of experience",
        "Premium products from OPI, Gelish, CND",
        "Hygienic practices with sterilized tools",
        "Easy parking available",
        "Serving Hebbal, Yelahanka & surrounding areas"
      ],
      popularServices: [
        { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Classic Manicure', price: '₹499', duration: '45 mins' },
        { name: 'Spa Pedicure', price: '₹799', duration: '60 mins' },
        { name: 'Nail Art (per nail)', price: '₹100', duration: '15 mins' },
        { name: 'Acrylic Extensions', price: '₹1,999', duration: '120 mins' },
        { name: 'Bridal Nail Package', price: '₹3,999', duration: '180 mins' }
      ]
    }
  },
  'varthur': {
    name: 'Varthur',
    fullName: 'Varthur & Sarjapur Road',
    slug: 'varthur',
    title: 'Best Nail Salon in Varthur & Sarjapur Road | Nail Extensions & Art',
    metaDescription: 'Top nail salon in Varthur & Sarjapur Road area. BLR Nail Club at CSK Mall offers nail extensions, gel nails, manicure, pedicure & lash services. Book your appointment!',
    keywords: 'nail salon Varthur, nail salon Sarjapur Road, best nail art Varthur, manicure pedicure Sarjapur, nail extensions Varthur, gel nails Sarjapur Road, beauty salon Varthur, nail spa Sarjapur, nails Whitefield area, nail salon East Bangalore',
    h1: 'Best Nail Salon in Varthur & Sarjapur Road',
    h2: 'Premium Nail Extensions & Beauty Services',
    address: 'CSK Mall, Gunjur, Varthur, Bengaluru 560087',
    phone: '+91 9741763377',
    googleProfileUrl: 'https://share.google/eL6tpHiSKY3ZcfRms',
    timings: '10:00 AM - 9:00 PM',
    hasStore: true,
    nearbyAreas: ['Whitefield', 'Marathahalli', 'Bellandur', 'Sarjapur', 'Gunjur', 'Carmelaram'],
    heroImage: 'https://images.pexels.com/photos/3997384/pexels-photo-3997384.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '12.9352',
    lng: '77.7315',
    content: {
      intro: "Welcome to BLR Nail Club at CSK Mall Varthur - serving the Sarjapur Road and East Bangalore community with exceptional nail and beauty services. Conveniently located in CSK Mall with ample parking and mall amenities.",
      whyChoose: [
        "Located in CSK Mall - easy access & parking",
        "Extended hours till 9 PM",
        "Serving Whitefield, Sarjapur, Bellandur areas",
        "Latest international nail trends",
        "Expert nail technicians",
        "Walk-in friendly with quick service options"
      ],
      popularServices: [
        { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Express Manicure', price: '₹399', duration: '30 mins' },
        { name: 'Luxury Pedicure', price: '₹999', duration: '75 mins' },
        { name: 'Ombre Nails', price: '₹1,599', duration: '100 mins' },
        { name: 'Lash Extensions', price: '₹2,499', duration: '90 mins' },
        { name: 'Nail Repair', price: '₹199', duration: '15 mins' }
      ]
    }
  },
  'sarjapur-road': {
    name: 'Sarjapur Road',
    fullName: 'Sarjapur Road',
    slug: 'sarjapur-road',
    title: 'Best Nail Salon on Sarjapur Road, Bangalore | Nails, Manicure, Pedicure',
    metaDescription: 'Looking for a nail salon on Sarjapur Road? Visit BLR Nail Club for premium nail extensions, gel nails, nail art, manicure & pedicure. Serving Sarjapur, Bellandur & HSR Layout.',
    keywords: 'nail salon Sarjapur Road, best nails Sarjapur, manicure pedicure Sarjapur Road, nail extensions Sarjapur, gel nails Sarjapur Road, beauty salon Sarjapur, nail art Sarjapur Road, nail spa HSR Layout, nails Bellandur, hair salon Sarjapur Road',
    h1: 'Best Nail Salon on Sarjapur Road',
    h2: 'Expert Nail Art, Extensions & Beauty Services',
    address: 'CSK Mall, Gunjur (Sarjapur Road), Bengaluru 560087',
    phone: '+91 9741763377',
    googleProfileUrl: 'https://share.google/eL6tpHiSKY3ZcfRms',
    timings: '10:00 AM - 9:00 PM',
    hasStore: true,
    nearbyAreas: ['HSR Layout', 'Bellandur', 'Haralur', 'Carmelaram', 'Kodathi', 'Dommasandra'],
    heroImage: 'https://images.pexels.com/photos/3997385/pexels-photo-3997385.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '12.9102',
    lng: '77.6846',
    content: {
      intro: "BLR Nail Club on Sarjapur Road brings premium nail services to South-East Bangalore. Our studio caters to the IT corridor crowd with flexible timings and quick service options perfect for busy professionals.",
      whyChoose: [
        "Perfect for IT professionals - extended evening hours",
        "Quick lunch-break services available",
        "Premium brands - OPI, Essie, Gelish",
        "Instagram-worthy nail art designs",
        "Group booking discounts",
        "Corporate tie-ups available"
      ],
      popularServices: [
        { name: 'Gel Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Quick Gel Polish', price: '₹599', duration: '30 mins' },
        { name: 'Express Pedicure', price: '₹499', duration: '30 mins' },
        { name: 'Nail Art Design', price: '₹100', duration: '15 mins' },
        { name: 'Acrylic Full Set', price: '₹1,999', duration: '120 mins' },
        { name: 'Mani-Pedi Combo', price: '₹1,199', duration: '90 mins' }
      ]
    }
  },
  'gunjur': {
    name: 'Gunjur',
    fullName: 'Gunjur',
    slug: 'gunjur',
    title: 'Best Nail Salon in Gunjur, Bangalore | Nail Art & Extensions Near Varthur',
    metaDescription: 'Visit BLR Nail Club in Gunjur (CSK Mall) for the best nail services. Expert nail extensions, gel nails, manicure, pedicure & nail art. Serving Gunjur, Varthur & surrounding areas.',
    keywords: 'nail salon Gunjur, best nails Gunjur, manicure pedicure Gunjur, nail extensions Gunjur, gel nails Gunjur, beauty salon Gunjur, nail art Gunjur Bangalore, nail spa near Gunjur, CSK Mall nail salon',
    h1: 'Best Nail Salon in Gunjur',
    h2: 'Premium Nail Services at CSK Mall',
    address: 'CSK Mall, Gunjur Main Road, Bengaluru 560087',
    phone: '+91 9741763377',
    googleProfileUrl: 'https://share.google/eL6tpHiSKY3ZcfRms',
    timings: '10:00 AM - 9:00 PM',
    hasStore: true,
    nearbyAreas: ['Varthur', 'Kadugodi', 'Whitefield', 'Hoodi', 'Immadihalli'],
    heroImage: 'https://images.pexels.com/photos/3997386/pexels-photo-3997386.jpeg?auto=compress&cs=tinysrgb&w=1920',
    lat: '12.9489',
    lng: '77.7462',
    content: {
      intro: "BLR Nail Club at CSK Mall Gunjur is your neighborhood nail studio offering world-class nail services. Whether you live in Gunjur, Varthur, or the surrounding areas, we're your go-to destination for beautiful nails.",
      whyChoose: [
        "Located in CSK Mall - shop while you wait",
        "Family-friendly environment",
        "Kids nail services available",
        "Loyalty program with rewards",
        "Free nail consultation",
        "Birthday special discounts"
      ],
      popularServices: [
        { name: 'Gel Nail Extensions', price: '₹1,499', duration: '90 mins' },
        { name: 'Kids Manicure', price: '₹299', duration: '20 mins' },
        { name: 'Spa Pedicure', price: '₹799', duration: '60 mins' },
        { name: 'Glitter Nails', price: '₹1,199', duration: '75 mins' },
        { name: 'Nail Strengthening', price: '₹399', duration: '30 mins' },
        { name: 'Party Nails Package', price: '₹2,499', duration: '120 mins' }
      ]
    }
  }
};

const LocationSEOPage = () => {
  const { locationSlug } = useParams();
  const location_path = useLocation();
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  // Get the slug from URL path mapping or use the param
  const slug = urlToSlugMap[location_path.pathname] || locationSlug;
  const location = locationSEOData[slug];
  
  if (!location) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-4">Location Not Found</h1>
          <Link to="/" className="text-[#B8860B] hover:underline">Go to Home</Link>
        </div>
      </div>
    );
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "NailSalon",
    "name": `BLR Nail Club ${location.name}`,
    "description": location.metaDescription,
    "url": `https://www.blrnailclub.in/nails-${location.slug}`,
    "telephone": location.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": location.address,
      "addressLocality": location.name,
      "addressRegion": "Karnataka",
      "postalCode": location.address.match(/\d{6}/)?.[0] || "560000",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": location.lat,
      "longitude": location.lng
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "10:00",
      "closes": "20:00"
    },
    "priceRange": "₹₹",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "444"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Nail Services",
      "itemListElement": location.content.popularServices.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.name,
          "description": `${service.name} service at BLR Nail Club ${location.name}`
        },
        "price": service.price.replace('₹', '').replace(',', ''),
        "priceCurrency": "INR"
      }))
    },
    "areaServed": location.nearbyAreas.map(area => ({
      "@type": "City",
      "name": area
    }))
  };

  return (
    <>
      <Helmet>
        <title>{location.title}</title>
        <meta name="description" content={location.metaDescription} />
        <meta name="keywords" content={location.keywords} />
        <link rel="canonical" href={`https://www.blrnailclub.in/nails-${location.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="business.business" />
        <meta property="og:url" content={`https://www.blrnailclub.in/nails-${location.slug}`} />
        <meta property="og:title" content={location.title} />
        <meta property="og:description" content={location.metaDescription} />
        <meta property="og:image" content={location.heroImage} />
        <meta property="business:contact_data:street_address" content={location.address} />
        <meta property="business:contact_data:locality" content={location.name} />
        <meta property="business:contact_data:region" content="Karnataka" />
        <meta property="business:contact_data:country_name" content="India" />
        <meta property="business:contact_data:phone_number" content={location.phone} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={location.title} />
        <meta name="twitter:description" content={location.metaDescription} />
        <meta name="twitter:image" content={location.heroImage} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="IN-KA" />
        <meta name="geo.placename" content={`${location.name}, Bangalore`} />
        <meta name="geo.position" content={`${location.lat};${location.lng}`} />
        <meta name="ICBM" content={`${location.lat}, ${location.lng}`} />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <main className="bg-black min-h-screen" data-testid={`location-seo-${location.slug}`}>
        {/* Hero Section */}
        <section className="relative h-[70vh] min-h-[500px]">
          <img
            src={location.heroImage}
            alt={`Nail Salon in ${location.name}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
          
          <div className="relative z-10 h-full flex flex-col justify-end pb-16 px-6 md:px-12 max-w-7xl mx-auto">
            {location.comingSoon && (
              <span className="inline-block bg-[#B8860B] text-black text-xs tracking-wider uppercase px-4 py-2 mb-4 w-fit">
                Coming Soon
              </span>
            )}
            <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light tracking-wide mb-4">
              {location.h1}
            </h1>
            <p className="text-white/70 text-lg md:text-xl font-light mb-8 max-w-2xl">
              {location.h2}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-[#B8860B] text-black px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
                data-testid="location-book-now"
              >
                Book Appointment
              </button>
              <a
                href={`tel:${location.phone}`}
                className="border border-white/30 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-light hover:bg-white/10 transition-colors"
              >
                Call {location.phone}
              </a>
              {location.googleProfileUrl && (
                <a
                  href={location.googleProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/30 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-light hover:bg-white/10 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  View on Google
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Location Info Bar */}
        <section className="bg-[#0a0a0a] border-y border-white/10 py-6">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-8 justify-center md:justify-between items-center">
            <div className="flex items-center gap-3">
              <MapPin className="text-[#B8860B]" size={20} />
              <span className="text-white/70 text-sm">{location.address}</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="text-[#B8860B]" size={20} />
              <span className="text-white/70 text-sm">{location.timings}</span>
            </div>
            <div className="flex items-center gap-3">
              <Star className="text-[#B8860B] fill-[#B8860B]" size={20} />
              <span className="text-white/70 text-sm">{location.rating || '4.8'} Rating ({location.reviewCount || '444'}+ Reviews)</span>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4">About Us</h2>
                <h3 className="text-white text-3xl md:text-4xl font-light mb-6">
                  Why Choose BLR Nail Club {location.name}?
                </h3>
                <p className="text-white/60 text-lg leading-relaxed mb-8">
                  {location.content.intro}
                </p>
                
                <ul className="space-y-4">
                  {location.content.whyChoose.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-[#B8860B] flex-shrink-0 mt-1" size={18} />
                      <span className="text-white/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-[#0a0a0a] border border-white/10 p-8 rounded-lg">
                <h3 className="text-white text-xl mb-6 flex items-center gap-2">
                  <Sparkles className="text-[#B8860B]" size={20} />
                  Popular Services in {location.name}
                </h3>
                
                <div className="space-y-4">
                  {location.content.popularServices.map((service, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-white/5">
                      <div>
                        <p className="text-white font-light">{service.name}</p>
                        <p className="text-white/40 text-sm">{service.duration}</p>
                      </div>
                      <span className="text-[#B8860B] font-medium">{service.price}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => setIsBookingModalOpen(true)}
                  className="w-full mt-8 bg-[#B8860B] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Serve */}
        <section className="py-16 px-6 md:px-12 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 text-center">Areas We Serve</h2>
            <h3 className="text-white text-2xl md:text-3xl font-light mb-8 text-center">
              Serving {location.name} & Nearby Areas
            </h3>
            
            <div className="flex flex-wrap justify-center gap-4">
              {location.nearbyAreas.map((area, index) => (
                <span
                  key={index}
                  className="px-6 py-3 bg-white/5 border border-white/10 text-white/70 text-sm hover:border-[#B8860B]/50 hover:text-[#B8860B] transition-colors"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-20 px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-white text-2xl md:text-3xl font-light mb-8 text-center">
              Best Nail Salon in {location.fullName}, Bangalore
            </h2>
            
            <div className="text-white/60 space-y-6 leading-relaxed">
              <p>
                Looking for the <strong className="text-white">best nail salon in {location.name}</strong>? BLR Nail Club is your premier destination for professional nail services in {location.fullName} and surrounding areas. Our expert nail technicians specialize in <strong className="text-white">nail extensions, gel nails, acrylic nails, nail art, manicure, and pedicure</strong> services.
              </p>
              
              <p>
                Whether you're searching for "<strong className="text-white">nail salon near me in {location.name}</strong>" or "<strong className="text-white">best manicure pedicure {location.name}</strong>", BLR Nail Club offers premium quality services at affordable prices. We use only the finest products from trusted brands like OPI, Gelish, and CND.
              </p>
              
              <p>
                Our {location.name} studio offers a wide range of services including <strong className="text-white">gel nail extensions, acrylic nail extensions, dip powder nails, chrome nails, ombre nails, French tips, and custom nail art</strong>. We also provide <strong className="text-white">lash extensions, hair services, and beauty treatments</strong>.
              </p>
              
              <p>
                Conveniently located {location.hasStore ? 'in' : 'near'} {location.name}, we serve customers from {location.nearbyAreas.join(', ')} and other nearby areas. Book your appointment today and experience why we're rated the <strong className="text-white">best nail salon in {location.name}, Bangalore</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 md:px-12 bg-[#B8860B]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-black text-3xl md:text-4xl font-light mb-4">
              Ready for Beautiful Nails?
            </h2>
            <p className="text-black/70 mb-8">
              Book your appointment at BLR Nail Club {location.name} today
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="bg-black text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-black/80 transition-colors"
              >
                Book Online
              </button>
              <a
                href={`https://wa.me/917204602211?text=Hi! I'd like to book an appointment at BLR Nail Club ${location.name}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-8 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#20BD5A] transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </section>

        {/* Other Locations */}
        <section className="py-16 px-6 md:px-12 bg-black">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-[#B8860B] text-xs tracking-[0.3em] uppercase mb-4 text-center">Other Locations</h2>
            <h3 className="text-white text-2xl font-light mb-8 text-center">
              Visit Our Other Studios
            </h3>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.values(locationSEOData)
                .filter(loc => loc.slug !== location.slug)
                .slice(0, 3)
                .map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/nails-${loc.slug}`}
                    className="group bg-[#0a0a0a] border border-white/10 p-6 hover:border-[#B8860B]/50 transition-colors"
                  >
                    <h4 className="text-white text-lg mb-2 group-hover:text-[#B8860B] transition-colors">
                      {loc.name}
                    </h4>
                    <p className="text-white/50 text-sm mb-4">{loc.address.split(',')[0]}</p>
                    <span className="text-[#B8860B] text-sm flex items-center gap-2">
                      View Location <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} />
    </>
  );
};

export { locationSEOData };
export default LocationSEOPage;
